import axios from 'axios';

import { BaseIngredientSortOrder } from 'enums/SortOrder/BaseIngredientSortOrder';
import { AllergenLogic } from 'enums/allergenLogic';
import { Characteristic } from 'enums/characteristic';
import { NutriScoreCategory } from 'enums/nutriScoreCategory';
import { SERVICEBASEURL } from 'enums/serviceBaseUrls';
import { Optional } from 'functions/promiseExtensions';
import { AllergenLogicMap } from 'types/Allergen/AllergenLogicMap';

export function callApi(request: Request): Promise<Optional<ResponseData>> {
	const url = `${SERVICEBASEURL.BACKEND}/BaseIngredient/Search`;
	return axios.post(url, request).toOptionalMapped((x) => x.data);
}

type PreferredAllergen = {
	id: string;
	logic: AllergenLogic;
};

export interface Request {
	allergenIds?: AllergenLogicMap | null;
	caloriesRange?: Range | null;
	carbonDioxideLabelIds?: string[] | null;
	carbonDioxideRange?: Range | null;
	categories?: string[] | null;
	characteristics?: string[] | null;
	nutriScoreLabelIds?: string[] | null;
	nutrientRanges?: NutrientRange[] | null;
	pageIndex?: number | null;
	pageSize?: number | null;
	preferredAllergens: PreferredAllergen[];
	preferredNutrientIds?: string[];
	searchTerm?: string | null;
	seasonIds?: string[] | null;
	showOnlyDeleted?: boolean | null;
	sortOrder?: BaseIngredientSortOrder | null;
	tagIds?: string[] | null;
}

export interface Response {
	data: ResponseData;
}

export type ResponseData = {
	data: ResponseBaseIngredient[];
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	minMaxCaloriesRange: Range;
	minMaxCarbonDioxideRange: Range;
	minMaxNutrientRanges: MinMaxNutrientRange[];
	pageIndex: number;
	pageSize: number;
	totalCount: number;
	totalPages: number;
};

export type ResponseBaseIngredient = {
	allergens: Allergen[];
	calories: number | null;
	carbonDioxideLabel: CarbonDioxideLabel;
	carbonDioxideValue: number | null;
	categories: Category[];
	characteristic: Characteristic;
	countOfIngredients: number;
	density: number | null;
	hasNutriScore: boolean;
	id: string;
	image: Image | null;
	lastEditedDateUtc: string;
	lastEditor: string | null;
	name: string;
	nutriScore: NutriScore;
	seasons: Season[];
	tags: Tag[];
};

export type NutrientRange = {
	id: string;
	maximum: number | null;
	minimum: number | null;
};

export type Range = {
	maximum: number | null;
	minimum: number | null;
};

export type MinMaxNutrientRange = {
	nutrientInfo: NutrientInfo;
	range: Range;
};

export type NutrientInfo = {
	id: string;
	name: string | null;
	nutrientCategory: NutrientCategory;
	sortOrder: number;
	unitShortNameSingular: string | null;
};

export type NutrientCategory = {
	id: string;
	name: string | null;
};

export type Image = {
	availableSizes: number[];
	id: string;
	url: string;
};

export type NutriScore = {
	color: string;
	label: string;
};

export type CarbonDioxideLabel = {
	color: string | null;
	label: string | null;
};

export type Season = {
	associatedMonthsFlag: number | null;
	display: boolean;
	iconSvgUrl: string | null;
	id: string;
	name: string | null;
};

export type Allergen = {
	display: boolean;
	iconSvgUrl: string | null;
	id: string;
	logic: AllergenLogic;
	name: string;
};

export type Tag = {
	display: boolean;
	iconSvgUrl: string | null;
	id: string;
	name: string;
	tagCategory: TagCategory;
};

export type TagCategory = {
	id: string;
	name: string | null;
};

export type Category = {
	id: string;
	name: string | null;
	nutriScoreCategory: NutriScoreCategory;
	parentName: string | null;
};
